import { Modal } from "./Modal";
import { IS_MAC } from "../hooks/useKeyboard";
import styles from "./KeyboardHelp.module.css";

interface KeyboardHelpProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Static reference modal listing every keyboard shortcut shotflux registers.
 * Triggered by `?` and the topbar help icon. Sections + bindings mirror
 * spec §9.2 exactly so this doubles as a documentation surface.
 *
 * `mod` is rendered as `⌘` on macOS and `Ctrl` elsewhere, using the same
 * detection the `useKeyboard` hook uses — keeps the help label consistent
 * with what the user actually has to press.
 */
export function KeyboardHelp({ open, onClose }: KeyboardHelpProps) {
  const mod = IS_MAC ? "⌘" : "Ctrl";

  // Keys are described as ordered tokens. A string token is rendered as a
  // <kbd>; "+" is a literal joiner between mod combos; " " is a literal
  // joiner between chord steps. Keeps the markup data-driven so changes
  // stay easy to audit against the spec.
  type Binding = { keys: ReadonlyArray<string | "+" | " ">; desc: string };
  type Group = { label: string; bindings: ReadonlyArray<Binding> };

  const groups: ReadonlyArray<Group> = [
    {
      label: "Global",
      bindings: [
        { keys: [mod, "+", "K"], desc: "Command palette" },
        { keys: [mod, "+", "P"], desc: "Project switcher" },
        { keys: ["?"], desc: "Show this help" },
      ],
    },
    {
      label: "Project / scene",
      bindings: [
        { keys: [mod, "+", "B"], desc: "Run breakdown" },
        { keys: [mod, "+", "E"], desc: "Export prompts" },
        { keys: ["g", " ", "p"], desc: "Go to projects" },
        { keys: ["g", " ", "s"], desc: "Go to scenes" },
        { keys: ["g", " ", "c"], desc: "Go to characters" },
      ],
    },
    {
      label: "Shot navigation (when scene editor has focus)",
      bindings: [
        { keys: ["j"], desc: "Next shot" },
        { keys: ["↓"], desc: "Next shot" },
        { keys: ["k"], desc: "Previous shot" },
        { keys: ["↑"], desc: "Previous shot" },
        { keys: ["Enter"], desc: "Expand shot" },
        { keys: ["Esc"], desc: "Collapse shot" },
        { keys: ["e"], desc: "Enhance shot" },
        { keys: ["c"], desc: "Copy prompt" },
      ],
    },
  ];

  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts">
      <div className={styles.root}>
        {groups.map((group) => (
          <section key={group.label} className={styles.group}>
            <h4 className={styles.groupLabel}>{group.label}</h4>
            <dl className={styles.list}>
              {group.bindings.map((b, i) => (
                <KeyRow key={`${group.label}-${i}`} keys={b.keys} desc={b.desc} />
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Modal>
  );
}

interface KeyRowProps {
  keys: ReadonlyArray<string | "+" | " ">;
  desc: string;
}

function KeyRow({ keys, desc }: KeyRowProps) {
  return (
    <>
      <dt className={styles.keyCell}>
        {keys.map((token, i) => {
          if (token === "+" || token === " ") {
            const label = token === "+" ? "+" : "then";
            return (
              <span key={i} className={styles.kbdJoiner} aria-hidden="true">
                {label}
              </span>
            );
          }
          return (
            <kbd key={i} className={styles.kbd}>
              {token}
            </kbd>
          );
        })}
      </dt>
      <dd className={styles.descCell}>{desc}</dd>
    </>
  );
}
